import type { SaisieContact, SaisiePartenaire, SaisieSite } from '@/shared/donnees/types';
import {
  erreursPresentes,
  exiger,
  verifierCodePostal,
  verifierEmail,
  verifierSiret,
  verifierTelephone,
} from './validation';

/**
 * Ce qu'une fiche doit contenir pour être complète, le même partout :
 * formulaires de création, fiches, import Excel.
 *
 * - Un client : un contact principal joignable — prénom, nom, téléphone
 *   (l'e-mail est facultatif : un exploitant n'a parfois qu'un portable) — et
 *   un site d'intervention.
 * - Un partenaire : un contact principal complet — prénom, nom, téléphone,
 *   e-mail (c'est là que partent l'invitation et les refus) —, au moins un
 *   métier et au moins un département d'intervention.
 */

/** Le nom à afficher d'un contact : « Thierry Lafon ». */
export function nomContact(c: { prenom?: string | undefined; nom: string }): string {
  return [c.prenom?.trim() ?? '', c.nom.trim()].filter(Boolean).join(' ');
}

/**
 * Un contact d'entreprise, ou un contact secondaire de partenaire : prénom,
 * nom et téléphone obligatoires, e-mail facultatif. `emailExige` : le contact
 * principal d'un partenaire, qu'on invite et qui reçoit les e-mails.
 */
export function erreursContactClient(
  c: Pick<SaisieContact, 'prenom' | 'nom' | 'telephone' | 'email'>,
  emailExige = false
) {
  return erreursPresentes({
    prenom: exiger(c.prenom, 'Le prénom est obligatoire.'),
    nom: exiger(c.nom, 'Le nom est obligatoire.'),
    telephone: verifierTelephone(c.telephone, true),
    email: verifierEmail(c.email, emailExige),
  });
}

export function erreursSite(s: SaisieSite) {
  return erreursPresentes({
    nom: exiger(s.nom, 'Nommez le site : « Hangar nord », « Séchoir à fourrage »…'),
    codePostal: verifierCodePostal(s.codePostal, true),
    commune: exiger(s.commune, 'La commune est obligatoire.'),
  });
}

export function erreursPartenaire(p: SaisiePartenaire) {
  return erreursPresentes({
    raisonSociale: exiger(p.raisonSociale, 'La raison sociale est obligatoire.'),
    siret: verifierSiret(p.siret),
    contactPrenom: exiger(p.contactPrenom, 'Le prénom du contact est obligatoire.'),
    contactNom: exiger(p.contactNom, 'Le nom du contact est obligatoire.'),
    telephone: verifierTelephone(p.telephone, true),
    email: verifierEmail(p.email, true),
    codePostal: verifierCodePostal(p.codePostal),
    etapes: p.etapes.length === 0 ? 'Choisissez au moins un métier.' : undefined,
    zones: p.zones.length === 0 ? 'Choisissez au moins un département d’intervention.' : undefined,
  });
}
